import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { inventoryAPI, deliveryAPI, auditLogAPI, labAPI, workerAPI } from '../../services/api';
import { Link } from 'react-router-dom';

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const { user, hasAnyRole } = useAuth();
  const [stats, setStats] = useState({
    lowStockItems: [],
    upcomingDeliveries: [],
    recentActivities: [],
    labTasks: [],
    workers: [],
  });
  const [loading, setLoading] = useState(true);
  

  const getItemName = (item) => {
    return i18n.language === 'ar' && item.item_name_ar ? item.item_name_ar : item.item_name;
  };

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const newStats = {
        lowStockItems: [],
        upcomingDeliveries: [],
        recentActivities: [],
        labTasks: [],
        workers: [],
      };

      // Load each data type independently
      if (hasAnyRole(['Admin', 'Inventory', 'Purchasing'])) {
        try {
          const response = await inventoryAPI.lowStock();
          newStats.lowStockItems = response.data || [];
        } catch (error) {
          console.error('Error loading low stock items:', error);
        }
      }

      if (hasAnyRole(['Admin', 'Sales'])) {
        try {
          const response = await deliveryAPI.upcoming(7);
          newStats.upcomingDeliveries = response.data.data || response.data || [];
        } catch (error) {
          console.error('Error loading upcoming deliveries:', error);
        }
      }

      if (hasAnyRole(['Admin', 'Accounting'])) {
        try {
          const response = await auditLogAPI.getAll({ per_page: 10 });
          newStats.recentActivities = response.data.data || [];
        } catch (error) {
          console.error('Error loading recent activities:', error);
        }
      }

      if (hasAnyRole(['Admin', 'Lab'])) {
        try {
          const response = await labAPI.getAll({ status: 'Pending,Assigned,In Progress' });
          newStats.labTasks = response.data.data || [];
        } catch (error) {
          console.error('Error loading lab tasks:', error);
        }

        try {
          const response = await workerAPI.getAll({ is_active: true });
          newStats.workers = response.data.data || [];
        } catch (error) {
          console.error('Error loading workers:', error);
        }

        
      }

      setStats(newStats);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [hasAnyRole]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  

  const gradientStyle = i18n.language === 'ar'
    ? 'linear-gradient(270deg, #000000 0%, #facc0c 100%)'
    : 'linear-gradient(90deg, #000000 0%, #facc0c 100%)';

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div style={{ background: gradientStyle }} className="rounded-2xl shadow-xl p-8 text-white">
        <h2 className="text-3xl font-bold mb-2">{t('dashboard.welcome')}, {user?.full_name}! 👋</h2>
        <p className="text-blue-100">{t('users.role')}: {t(`users.roles.${user?.role}`)}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {hasAnyRole(['Admin', 'Inventory', 'Purchasing']) && (
          <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-red-500">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.lowStock')}</h3>
              <div className="h-12 w-12 bg-red-100 rounded-lg flex items-center justify-center">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>
            {loading ? (
              <div className="animate-pulse space-y-2">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
            ) : stats.lowStockItems.length > 0 ? (
              <div className="space-y-3">
                {stats.lowStockItems.slice(0, 3).map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{getItemName(item)}</p>
                      <p className="text-sm text-gray-600">{item.quantity} {item.unit} remaining</p>
                    </div>
                    <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded">{t('inventory.lowStock')}</span>
                  </div>
                ))}
                {stats.lowStockItems.length > 3 && (
                  <Link to="/inventory" className="block text-center text-sm text-blue-600 hover:text-blue-800 font-medium mt-2">
                    {t('common.viewDetails')} ({stats.lowStockItems.length}) →
                  </Link>
                )}
              </div>
            ) : (
              <p className="text-gray-600">{t('common.noData')}</p>
            )}
          </div>
        )}

        {hasAnyRole(['Admin', 'Sales']) && (
          <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-blue-500">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.upcomingDeliveries')}</h3>
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </div>
            </div>
            {loading ? (
              <div className="animate-pulse space-y-2">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
            ) : stats.upcomingDeliveries.length > 0 ? (
              <div className="space-y-3">
                {stats.upcomingDeliveries.slice(0, 3).map((delivery) => (
                  <div key={delivery.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{delivery.sales_order?.order_number || `Order #${delivery.sales_order_id}`}</p>
                      <p className="text-sm text-gray-600">
                        {delivery.sales_order?.customer?.name && `${delivery.sales_order.customer.name} • `}
                        {new Date(delivery.scheduled_date).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded ${delivery.status === 'Scheduled' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {t(`deliveries.statuses.${delivery.status}`)}
                    </span>
                  </div>
                ))}
                {stats.upcomingDeliveries.length > 3 && (
                  <Link to="/deliveries" className="block text-center text-sm text-blue-600 hover:text-blue-800 font-medium mt-2">
                    {t('common.viewDetails')} ({stats.upcomingDeliveries.length}) →
                  </Link>
                )}
              </div>
            ) : (
              <p className="text-gray-600">{t('deliveries.noDeliveries')}</p>
            )}
          </div>
        )}

        {hasAnyRole(['Admin', 'Accounting']) && (
          <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-purple-500">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.recentActivities')}</h3>
              <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
            {loading ? (
              <div className="animate-pulse space-y-2">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
            ) : stats.recentActivities.length > 0 ? (
              <div className="space-y-3">
                {stats.recentActivities.slice(0, 3).map((log) => (
                  <div key={log.id} className="p-3 bg-purple-50 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{log.username}</p>
                        <p className="text-xs text-gray-600 mt-1">{log.description}</p>
                      </div>
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-semibold rounded ml-2">
                        {t(`auditLogs.actions.${log.action}`)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {new Date(log.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
                <Link to="/audit-logs" className="block text-center text-sm text-blue-600 hover:text-blue-800 font-medium mt-2">
                  {t('common.viewDetails')} →
                </Link>
              </div>
            ) : (
              <p className="text-gray-600">{t('common.noData')}</p>
            )}
          </div>
        )}

        {hasAnyRole(['Admin', 'Lab']) && (
          <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-orange-500">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.labTasks')}</h3>
              <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <svg className="h-6 w-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
            </div>
            {loading ? (
              <div className="animate-pulse space-y-2">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
            ) : stats.labTasks.length > 0 ? (
              <div className="space-y-3">
                {stats.labTasks.slice(0, 3).map((task) => (
                  <div key={task.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{task.lab_number}</p>
                      <p className="text-sm text-gray-600">SO-{task.sales_order_id}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded ${
                      task.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' : 
                      task.status === 'Assigned' ? 'bg-blue-100 text-blue-800' : 
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {task.status}
                    </span>
                  </div>
                ))}
                {stats.labTasks.length > 3 && (
                  <Link to="/lab" className="block text-center text-sm text-blue-600 hover:text-blue-800 font-medium mt-2">
                    {t('common.viewDetails')} ({stats.labTasks.length}) →
                  </Link>
                )}
              </div>
            ) : (
              <p className="text-gray-600">{t('common.noData')}</p>
            )}
          </div>
        )}

        {hasAnyRole(['Admin', 'Lab']) && (
          <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-green-500">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.activeWorkers')}</h3>
              <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
            {loading ? (
              <div className="animate-pulse space-y-2">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
            ) : stats.workers.length > 0 ? (
              <div className="space-y-3">
                <div className="mb-3 text-2xl font-bold text-green-600">{stats.workers.length}</div>
                {stats.workers.slice(0, 2).map((worker) => (
                  <div key={worker.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{worker.full_name}</p>
                      <p className="text-sm text-gray-600">{worker.skills?.split(',')[0]}</p>
                    </div>
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">{t('dashboard.status.active')}</span>
                  </div>
                ))}
                {stats.workers.length > 2 && (
                  <Link to="/workers" className="block text-center text-sm text-blue-600 hover:text-blue-800 font-medium mt-2">
                    {t('common.viewDetails')} ({stats.workers.length}) →
                  </Link>
                )}
              </div>
            ) : (
              <p className="text-gray-600">{t('common.noData')}</p>
            )}
          </div>
        )}

        
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">{t('dashboard.quickActions')}</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {hasAnyRole(['Admin', 'Sales']) && (
            <Link to="/sales-orders/new" className="flex flex-col items-center p-4 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg hover:shadow-md transition-all duration-200">
              <div className="h-12 w-12 rounded-lg flex items-center justify-center mb-2" style={{ background: '#facc0c' }}>
                <svg className="h-6 w-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <span className="text-sm font-medium text-gray-700">{t('salesOrders.addOrder')}</span>
            </Link>
          )}
          {hasAnyRole(['Admin', 'Inventory']) && (
            <Link to="/inventory/new" className="flex flex-col items-center p-4 bg-gradient-to-br from-green-50 to-teal-50 rounded-lg hover:shadow-md transition-all duration-200">
              <div className="h-12 w-12 bg-green-600 rounded-lg flex items-center justify-center mb-2">
                <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <span className="text-sm font-medium text-gray-700">{t('inventory.addItem')}</span>
            </Link>
          )}
          {hasAnyRole(['Admin', 'Purchasing']) && (
            <Link to="/purchases/new" className="flex flex-col items-center p-4 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg hover:shadow-md transition-all duration-200">
              <div className="h-12 w-12 bg-yellow-600 rounded-lg flex items-center justify-center mb-2">
                <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <span className="text-sm font-medium text-gray-700">{t('purchases.addPurchase')}</span>
            </Link>
          )}
          {hasAnyRole(['Admin', 'Sales']) && (
            <Link to="/customers/new" className="flex flex-col items-center p-4 bg-gradient-to-br from-pink-50 to-red-50 rounded-lg hover:shadow-md transition-all duration-200">
              <div className="h-12 w-12 bg-pink-600 rounded-lg flex items-center justify-center mb-2">
                <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <span className="text-sm font-medium text-gray-700">{t('customers.addCustomer')}</span>
            </Link>
          )}
          {hasAnyRole(['Admin', 'Lab']) && (
            <Link to="/lab/new" className="flex flex-col items-center p-4 bg-gradient-to-br from-orange-50 to-red-50 rounded-lg hover:shadow-md transition-all duration-200">
              <div className="h-12 w-12 bg-orange-600 rounded-lg flex items-center justify-center mb-2">
                <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <span className="text-sm font-medium text-gray-700">{t('lab.addTask')}</span>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
